// Driver-name privacy.
//
// iRacing's third-party terms forbid displaying a member's display name or
// customer id (cust_id) without THAT member's explicit, affirmative, and
// revocable consent. Members opt in from the Account page after linking
// their iRacing account.
//
// The backend enforces this: every API response replaces non-consenting
// members' names with "Hidden Driver", sets their cust_id to null, and adds
// `member_hidden: true`. The frontend just renders what it gets, and must
// never link to or print a cust_id that came back null.
//
// A signed-in user always sees their own name. Pass authHeaders() on API
// calls so the backend knows who is viewing.
//
// `iracingLinked` (localStorage) only tracks whether the visitor has linked
// their own account; it no longer reveals anyone else's name.

import { useEffect, useState } from "react";

const STORAGE_KEY = "iracingLinked";
const CHANGE_EVENT = "iracinglink-changed";
export const MEMBER_CONSENT_CHANGE_EVENT = 'memberconsent-changed';

export const HIDDEN_DRIVER_LABEL = "Hidden Driver";

export function isIracingLinked() {
        if (typeof window === "undefined") return false;
        return localStorage.getItem(STORAGE_KEY) === "true";
}

// Call this after writing/clearing the iracingLinked flag in the same tab
// so subscribed components re-render without waiting for a navigation.
export function notifyIracingLinkChanged() {
        if (typeof window === "undefined") return;
        window.dispatchEvent(new Event(CHANGE_EVENT));
}

// The timestamp signals other tabs to reload the saved choice. It is only a
// notification; consent itself is always read from the authenticated API.
export function notifyMemberConsentChanged() {
        if (typeof window === 'undefined') return;
        localStorage.setItem('memberConsentChangedAt', String(Date.now()));
        window.dispatchEvent(new Event(MEMBER_CONSENT_CHANGE_EVENT));
}

export function useIracingLinked() {
        const [linked, setLinked] = useState(isIracingLinked);

        useEffect(() => {
                const handler = () => setLinked(isIracingLinked());
                window.addEventListener("storage", handler);
                window.addEventListener(CHANGE_EVENT, handler);
                return () => {
                        window.removeEventListener("storage", handler);
                        window.removeEventListener(CHANGE_EVENT, handler);
                };
        }, []);

        return linked;
}

// Names are redacted server-side; this only supplies the label for anything
// missing. The second argument is ignored (kept so existing call sites work).
export function maskDriverName(name) {
        return name || HIDDEN_DRIVER_LABEL;
}

// True when a row identifies a member who may be shown and linked to.
export function isVisibleDriver(custId) {
        return custId !== null && custId !== undefined && custId !== "";
}

// Authorization header for the signed-in user (empty object if signed out).
export function authHeaders() {
        if (typeof window === "undefined") return {};
        const token = localStorage.getItem("token");
        return token ? { Authorization: `Bearer ${token}` } : {};
}
